/**
 * <banner-video> — a looping background video with one pause/play control.
 *
 * Three things a background video has to get right, all handled here:
 *
 * - Autoplay can be refused. Muted + playsinline satisfies every current browser,
 *   but a data-saver setting or a battery-saver mode can still block the play(),
 *   so the button is set from what the video is actually doing rather than from
 *   what the markup asked for.
 * - Someone who asked for less motion should not be handed a moving background.
 *   The video is paused up front in that case, and the control lets them start it
 *   anyway if they want to.
 * - Off screen, it should stop. A hero at the top of a long page has no business
 *   decoding frames once the reader has scrolled past it — but only playback the
 *   visitor did not pause is resumed on the way back.
 */
(() => {
  class BannerVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-banner-video-toggle]');
      if (!this.video || !this.toggle) return;

      // Set here rather than in Liquid: these are playback details, and leaving
      // them to the attribute list invites a merchant-facing setting for each.
      this.video.muted = true;
      this.video.disablePictureInPicture = true;

      this.pausedByUser = false;

      this.onToggle = () => {
        if (this.video.paused) {
          this.pausedByUser = false;
          this.video.play().catch(() => {});
        } else {
          this.pausedByUser = true;
          this.video.pause();
        }
      };

      this.onState = () => this.#sync();

      this.toggle.addEventListener('click', this.onToggle);
      this.video.addEventListener('play', this.onState);
      this.video.addEventListener('pause', this.onState);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.pausedByUser = true;
        this.video.autoplay = false;
        this.video.pause();
      }

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (!this.pausedByUser) this.video.play().catch(() => {});
          } else if (!this.video.paused) {
            this.video.pause();
          }
        },
        {
          // Keep playing until the banner is well clear of the screen. At a tenth
          // of the banner it stopped while a strip of it was still in view, which
          // reads as the video breaking rather than as a considerate pause.
          // The margin also starts it just before it arrives, so it is already
          // running by the time it is on screen.
          threshold: 0,
          rootMargin: '25% 0px'
        }
      );
      this.observer.observe(this);

      this.#sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.toggle?.removeEventListener('click', this.onToggle);
      this.video?.removeEventListener('play', this.onState);
      this.video?.removeEventListener('pause', this.onState);
    }

    #sync() {
      this.toggle.classList.toggle('is-paused', this.video.paused);
    }
  }

  if (!customElements.get('banner-video')) {
    customElements.define('banner-video', BannerVideo);
  }
})();
